import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgesGroupSettings, BadgesSiteSettings, SettingsResponse } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Button, Card, CheckboxField, SelectField, Spinner } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToastOnSuccess } from '../../components/Toast';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { useTemplates } from './api';

type Draft = Record<string, unknown>;

/**
 * The badge settings of a site (default design, label warning) or a group (print mode, design).
 * Registered with `registerSettingsCard('badges', ...)`; the server enforces who may save.
 */
export function BadgeSettingsCard({ scope, scopeId, moduleKey, canEdit }: SettingsCardProps) {
  const qc = useQueryClient();
  const canManageDesigns = useCan('badges.manage');
  const base = `/${scope}/${scopeId}/settings`;
  const key = ['settings', base, moduleKey];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`${base}/${moduleKey}`, { schema: SettingsResponse }) });
  const templates = useTemplates('active', scope === 'sites' ? scopeId : undefined);
  const [draft, setDraft] = useState<Draft | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.values);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => {
      const keys = scope === 'sites' ? (['defaultTemplateId', 'mediaWarning'] as const) : (['print', 'templateId'] as const);
      const body: Draft = {};
      for (const k of keys) if (draft && draft[k] !== q.data?.values[k]) body[k] = draft[k];
      return api.patch(`${base}/${moduleKey}`, { body, schema: SettingsResponse });
    },
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      setDraft(r.values);
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data || !draft) return <Spinner />;

  const parsed = scope === 'sites' ? BadgesSiteSettings.safeParse(draft) : BadgesGroupSettings.safeParse(draft);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const changed = JSON.stringify(draft) !== JSON.stringify(q.data.values);
  const designs = templates.data ?? [];
  const designOptions = designs.map((d) => (
    <option key={d.id} value={d.id}>
      {d.name}
    </option>
  ));
  const toId = (v: string) => (v === '' ? null : v);

  return (
    <Card
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || !parsed.success || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        {save.error ? <ErrorLine error={save.error} /> : null}
        {scope === 'sites' ? (
          <>
            <SelectField
              label={t('badgeUi.settings.defaultTemplate')}
              value={(draft['defaultTemplateId'] as string | null) ?? ''}
              disabled={!canEdit}
              onChange={(e) => set('defaultTemplateId', toId(e.target.value))}
            >
              <option value="">{t('badgeUi.settings.noBadge')}</option>
              {designOptions}
            </SelectField>
            <CheckboxField
              label={t('badgeUi.settings.mediaWarning')}
              checked={draft['mediaWarning'] === true}
              disabled={!canEdit}
              onChange={(e) => set('mediaWarning', e.target.checked)}
            />
          </>
        ) : (
          <>
            <SelectField label={t('badgeUi.settings.print')} value={String(draft['print'] ?? 'auto')} disabled={!canEdit} onChange={(e) => set('print', e.target.value)}>
              <option value="never">{t('badgeUi.settings.print.never')}</option>
              <option value="auto">{t('badgeUi.settings.print.auto')}</option>
              <option value="ask">{t('badgeUi.settings.print.ask')}</option>
            </SelectField>
            <SelectField
              label={t('badgeUi.settings.template')}
              value={(draft['templateId'] as string | null) ?? ''}
              disabled={!canEdit || draft['print'] === 'never'}
              onChange={(e) => set('templateId', toId(e.target.value))}
            >
              <option value="">{t('badgeUi.settings.siteDefault')}</option>
              {designOptions}
            </SelectField>
          </>
        )}
        {canManageDesigns ? (
          <p className="text-sm">
            <Link to="/manage/badges" className="text-brand underline">
              {t('badgeUi.settings.manageLink')}
            </Link>
          </p>
        ) : null}
      </div>
    </Card>
  );
}
